import { GetSubsidiesFromPronovoRequest, UpdatePhotovoltaicsRequest } from 'api/dtos/building/building/input';
import { IPvTariffResponse } from 'api/dtos/building/building/output';
import { ProjectIdRequest } from 'api/dtos/general/request';
import {
  GetProjectEnergyResponse,
  GetProjectIrradiationResponse,
  UpdatePhotovoltaicsResponse,
} from 'api/dtos/project/response';
import { ILngLat } from 'domain/types/location/coordinates';

import { backendApi } from 'state/services/backend/api';
import { EBackendTag } from 'state/services/backend/types';
import { RequestMethod } from 'types/request';

export const projectPhotovoltaicsApiSlice = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjectIrradiation: builder.query<GetProjectIrradiationResponse, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project/${projectId}/energy/irradiation/`,
        method: RequestMethod.GET,
      }),
      providesTags: [EBackendTag.IRRADIATION, EBackendTag.INVALID_AFTER_SAVE],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const query = await queryFulfilled;

          if (query.data) {
            dispatch(projectPhotovoltaicsApiSlice.util.invalidateTags([EBackendTag.ENERGY]));
          }
        } catch {}
      },
    }),
    getProjectPhotovoltaics: builder.query<GetProjectEnergyResponse | null, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project/${projectId}/energy/photovoltaics`,
        method: RequestMethod.GET,
      }),
      providesTags: [EBackendTag.ENERGY],
    }),
    updateProjectPhotovoltaics: builder.mutation<UpdatePhotovoltaicsResponse, UpdatePhotovoltaicsRequest>({
      query: ({ projectId, buildings }) => ({
        url: `/api/project/${projectId}/energy/photovoltaics`,
        method: RequestMethod.POST,
        body: buildings,
      }),
      invalidatesTags: [
        EBackendTag.ENERGY,
        EBackendTag.BUILDINGS,
        EBackendTag.PROJECT_FINANCE,
        EBackendTag.COSTS,
        EBackendTag.INCOMES,
      ],
    }),
    getSubsidiesFromPronovo: builder.query<number, GetSubsidiesFromPronovoRequest>({
      query: ({ projectId, building }) => ({
        url: `/api/solar/subsidies/pronovo/${projectId}`,
        method: RequestMethod.POST,
        body: building,
      }),
    }),
    getGridPriceFromPvTariff: builder.query<IPvTariffResponse, ILngLat>({
      query: ({ lng, lat }) => ({
        url: `/api/solar/pv-tariff/${lng}/${lat}`,
        method: RequestMethod.GET,
      }),
    }),
  }),
});

export const {
  useGetProjectIrradiationQuery,
  useGetProjectPhotovoltaicsQuery,
  useUpdateProjectPhotovoltaicsMutation,
  useLazyGetSubsidiesFromPronovoQuery,
  useGetGridPriceFromPvTariffQuery,
} = projectPhotovoltaicsApiSlice;

export const useGetProjectIrradiationQueryState =
  projectPhotovoltaicsApiSlice.endpoints.getProjectIrradiation.useQueryState;
export const useGetProjectPhotovoltaicsQueryState =
  projectPhotovoltaicsApiSlice.endpoints.getProjectPhotovoltaics.useQueryState;
